import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Paper, Button, Box, FormHelperText } from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import axios from '../utils/api';
import ServiceProviderFormFields from './ServiceProviderFormFields';
import {
  ServiceProviderFormState,
  ServiceProviderValidationErrors,
  emptyServiceProviderForm,
  validateServiceProviderForm,
  buildServiceProviderPayload,
  scrollServiceProviderToFirstError,
} from './serviceProviderShared';

const AddServiceProviderForm: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<ServiceProviderFormState>(emptyServiceProviderForm());
  const [errors, setErrors] = useState<ServiceProviderValidationErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clearError = (field: keyof ServiceProviderValidationErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async () => {
    const validationErrors = validateServiceProviderForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollServiceProviderToFirstError(validationErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post('/api/service-providers', buildServiceProviderPayload(form));
      navigate('/management');
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || 'Failed to create agency.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Add Agency</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/management')}>Back</Button>
      </Box>

      <form onSubmit={(e) => e.preventDefault()}>
        <ServiceProviderFormFields form={form} setForm={setForm} errors={errors} clearError={clearError} />

        {submitError && (
          <Box sx={{ mt: 2 }}>
            <FormHelperText error sx={{ fontSize: '1rem' }}>{submitError}</FormHelperText>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} startIcon={<Save />} size="large">
            {submitting ? 'Saving...' : 'Create Agency'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddServiceProviderForm;
